/**
 * Connor — the appetite assistant, docked bottom-right across the hub.
 *
 * Modelled on the Salesforce appetite chat, with one deliberate difference: it
 * never surfaces "markets to consider pursuing". Members should see carriers AAS
 * is actually appointed with; naming a market we can't place with sends an agent
 * to chase an appointment we don't have.
 *
 * It is a guided flow, not a search box, and it forks on intent at the very first
 * turn. Some agents want a name and nothing else; some want walking through the
 * whole submission. Both roads start at appetite — you can't pick a supplemental
 * until you know the class — so the fork changes what happens *after* the answer,
 * not before it.
 *
 * What is real today: the appetite answer itself (Salesforce class map, 344
 * appointed carriers, 368 appetite guides, with citations), and the supplemental
 * hand-off for the handful of classes we hold forms for. What is not yet built —
 * the fillable web form, the link out to the insured, the packaged submission —
 * Connor says plainly rather than implying. A confident invented answer costs a
 * member a live submission.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { GLOSSARY, HUB_MODULES, searchGlossary, type AppetiteAnswer, type GlossaryTerm } from '@commpass/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { ApiError, api } from '../lib/api';
import { AppetiteAnswerCard } from './AppetiteAnswerCard';
import { ConnorPeek, ConnorStanding, YardSign } from './Pelican';
import { ChatText } from './ChatText';
import { connorTurn } from '../lib/connorStream';
import {
  clearConnorSession,
  loadConnorSession,
  saveConnorSession,
} from '../lib/connorSession';
import catalog from '../data/supplementals.json';
import { ago, loadRecent, rememberSearch, type RecentSearch } from '../lib/recentSearches';
import { newFormHref } from '../lib/newForm';

/**
 * `intent` is the fork: appetite only, or hold my hand through the submission.
 * Everything from `ask` to `answered` is identical either way — only what Connor
 * offers next differs.
 */
type Step =
  | 'intent'
  | 'recent'
  | 'know-markets'
  | 'how-quoting'
  | 'pick-form'
  | 'outcome'
  | 'ask-paper'
  /** Asked from the menu: the whole message is a term to define. */
  | 'ask-term'
  /**
   * Starting a request: which kind, then the insured, then the carrier.
   *
   * ⚠️ THREE STEPS AND THEN THE REAL PAGE. Connor collects only what identifies
   * the work and hands it over — he does not ask for the policy period, the
   * lines wanted, the signature block or the recipients, because the page
   * already asks for those with validation, the carrier contact book and a
   * letter preview attached. A wizard squeezed into a 380px bubble is worse
   * than the page built for it.
   */
  | 'start-kind'
  | 'start-insured'
  | 'start-carrier'
  | 'ask'
  | 'thinking'
  | 'answered'
  | 'offered-form'
  | 'routing'
  /**
   * Relay mode: everything typed goes to a person at Agents Alliance instead of
   * to the appetite engine, and their answers arrive in this same window.
   *
   * ⚠️ ONE CHAT BOX, NOT TWO (Josh, 2026-08-31). A second bubble for "talk to a
   * human" would mean a member deciding, before they have typed anything, which
   * of two things can answer them — and they cannot know that. So the escalation
   * lives inside Connor: he answers what he can, and hands the same conversation
   * to a person when he can't.
   */
  | 'human'
  | 'done';
type Intent = 'appetite' | 'guided';

/**
 * What Connor can hand you off to.
 *
 * `appetite` and `guided` he answers himself; the rest are doors to other parts
 * of the hub. He opens the door and gets out of the way rather than trying to
 * reimplement each tool inside a chat window — a wizard squeezed into a
 * 380px-wide bubble is worse than the page built for it.
 */
type Task =
  | Intent
  | 'portal'
  | 'commpass'
  | 'supplemental'
  | 'inspector'
  | 'recent'
  | 'paper'
  | 'glossary'
  | 'start'
  | 'human';

/**
 * ⚠️ THE MENU IS NO LONGER THE WHOLE INTERFACE (Josh, 2026-09-07: "Does connor
 * need to be so packed with quick buttons?"). Eleven buttons made the box read
 * as a directory you navigate rather than something you talk to, and most of
 * them were duplicating what typing already does — appetite is what free text
 * already goes to, "which markets carry X's paper" is pattern-matched, and any
 * "what does X mean" reaches the glossary.
 *
 * So `upfront: true` marks the ones typing genuinely cannot reach: they are
 * navigations and hand-offs, not questions. The rest stay one click away behind
 * "Something else", because removing a door somebody has learned is worse than
 * showing one too many.
 */
const MENU: {
  task: Task;
  label: string;
  primary?: boolean;
  upfront?: boolean;
  /** Withheld until a lookup has come back empty. See the 'human' entry. */
  needsEffort?: boolean;
}[] = [
  { task: 'appetite', label: 'Appetite guidance' },
  { task: 'portal', label: 'Build a commercial submission', upfront: true },
  // Named for the product an agent already knows, not the abstraction.
  { task: 'commpass', label: 'Send a submission to AAS (COMM-PASS)' },
  { task: 'supplemental', label: 'Find or send a supplemental' },
  // Josh, 2026-08-31: asked constantly, and until now only reachable by
  // guessing that you could type it.
  { task: 'paper', label: "Which markets carry a carrier's paper?" },
  // Josh, 2026-09-05: "I want Connor to be able to answer from the glossary."
  // Listed for the same reason 'paper' was — he can already answer it typed,
  // and an ability nobody knows about is one nobody uses.
  { task: 'glossary', label: 'What does a term mean?' },
  // Josh, 2026-09-05: "I want Connor conversational and to be able to start
  // every programme's process via Connor."
  { task: 'start', label: 'Start a request for me', primary: true, upfront: true },
  { task: 'inspector', label: 'Analyze an account', upfront: true },
  { task: 'guided', label: 'Walk me through the whole thing' },
  /*
    Last on purpose, and no longer shown until it has been earned.

    ⚠️ IT APPEARS ONLY AFTER A LOOKUP HAS COME BACK EMPTY (Josh, 2026-09-09: it
    "should only show up if the user isn't able to locate what they're looking
    for after a diligent effort"). Offered upfront it was the cheapest button in
    a box full of capable ones, and the cheapest button wins — Kylie, on the same
    build: "not even on this page … just keep pressing zero till you get a
    human." Connor answers appetite, paper, our book, the glossary, the forms and
    now AMS360; a member who reaches for a colleague first never finds that out.

    ⚠️ EARNED, NOT REMOVED. Typing for a person still works at every point:
    ask_the_desk is a tool the model can always reach, the sidebar's "Need a
    hand?" has not moved, and nobody should have to argue with a chatbot to get a
    colleague. This takes away a shortcut, not the exit.
  */
  { task: 'human', label: 'Chat with someone at Agents Alliance', needsEffort: true },
];

/**
 * Questions asked before the desk is offered regardless of how they went.
 *
 * Three, because two is a pair of tries and four is a member being managed. It
 * is deliberately a small number: the point is that a colleague should not be
 * the first thing anybody clicks, not that they should have to work for one.
 */
const ASK_BEFORE_DESK = 3;

/**
 * The request types Connor can start, taken from the registry.
 *
 * ⚠️ GENERATED, NOT LISTED. These are exactly the modules that live inside the
 * Requests Center, so a fourth request type becomes something Connor can start
 * by declaring `within: 'requests'` — the same field that already gives it a
 * button on that page. A hand-written list here is the copy that gets forgotten.
 */
const STARTABLE = HUB_MODULES.filter((m) => m.within === 'requests');

const INSPECTOR = HUB_MODULES.find((m) => m.id === 'inspector');
/** Where a full glossary entry lives, from the registry rather than typed here. */
const GLOSSARY_HREF = HUB_MODULES.find((m) => m.id === 'glossary')?.href ?? '/hub/glossary';

/**
 * Every abbreviation the glossary knows, lowercased.
 *
 * Built from the data rather than listed, so a term gaining a `short` becomes
 * answerable here without anybody remembering this file exists.
 */
const ABBREVIATIONS = new Set(
  GLOSSARY.filter((t) => t.short).map((t) => t.short!.toLowerCase()),
);

interface Msg {
  from: 'you' | 'aas';
  text?: string;
  /** An answer renders as a card, not a bubble — it's a table, not a sentence. */
  answer?: AppetiteAnswer;
}

interface FormRow {
  title: string;
  url: string;
}

const BY_CLASS = catalog.byClass as Record<string, FormRow[]>;

/**
 * The cloud, as one closed path on a 320×150 grid.
 *
 * Lobes across the top and down each side, a gently scalloped bottom, and the
 * speech tail dropping from the lower right toward Connor — he stands to the
 * right of the bubble, so a centred or left tail would point at nothing.
 *
 * ⚠️ ONE PATH, DRAWN TWICE. The fill and the shading share this constant rather
 * than each holding a copy: two hand-maintained copies of a 20-command path is
 * two things that silently stop matching the first time one is nudged.
 */
const CLOUD_D =
  'M 46 126 ' +
  'C 22 126 10 110 17 94 ' +
  'C 1 78 10 55 32 51 ' +
  'C 35 25 66 12 89 25 ' +
  'C 104 3 143 2 158 22 ' +
  'C 178 3 215 10 223 33 ' +
  'C 251 26 281 42 280 65 ' +
  'C 306 70 314 95 298 110 ' +
  'C 301 124 289 134 273 132 ' +
  'L 252 132 ' +
  'C 256 140 258 145 259 150 ' +
  'C 249 143 240 137 231 132 ' +
  'C 205 137 176 137 150 131 ' +
  'C 116 137 75 135 46 126 Z';

/**
 * What Connor says from behind the wall.
 *
 * ⚠️ Nothing here may promise that a carrier wants a risk. This is appetite
 * *guidance* — we surface what our guides indicate, and a carrier is free to
 * decline anything. Copy that says "I'll tell you who wants it" writes a cheque
 * underwriting hasn't signed, and contradicts the disclaimer directly below it.
 * Keep the verbs to pointing, showing and indicating.
 *
 * The first line introduces him by name; after that he's a known quantity and
 * repeating it every 35 seconds would wear thin.
 */
const OPENERS = [
  // He introduces himself once per rotation, then advertises what he can do.
  "Hi, I'm Connor. What type of risk are you trying to write?",
  'You can ask me… which of our markets fit a risk.',
  "You can ask me… who carries a carrier's paper.",
  'You can ask me… what any commercial insurance term means.',
  'You can ask me… to start a loss run request for you.',
  'You can ask me… to put you through to a person at Agents Alliance.',
];

/**
 * What he opens with on each page.
 *
 * ⚠️ ONLY WHAT HE CAN ACTUALLY DO. It is tempting to have him say "need help
 * requesting loss runs?" on Loss Runs, but Connor cannot request one — his menu
 * answers appetite and opens doors to other pages, and an opener that promises
 * more than the menu delivers teaches a member the mascot is decoration. So the
 * page line either names something on his menu (a form, a class, the desk) or
 * he keeps to appetite, which he does answer.
 *
 * Longest path first when matching, or /hub would swallow every child route.
 */
const PAGE_OPENERS: { path: string; lines: string[] }[] = [
  {
    path: '/hub/supplementals',
    lines: [
      "Hi, I'm Connor. Need help finding a form?",
      'Tell me the class and I’ll say which supplemental to use.',
    ],
  },
  {
    // He can genuinely answer this one now, so the opener is allowed to offer
    // it — the rule above is that the line must not promise more than he does.
    path: '/hub/glossary',
    lines: [
      "Hi, I'm Connor. Ask me what a term means and I'll read you the entry.",
      "Not sure which of two terms you want? Ask me for the difference.",
    ],
  },
  {
    path: '/hub/binders',
    lines: [
      "Hi, I'm Connor. Ready to bind? Give me the insured and the carrier and I'll start it.",
      'If a market has quoted and you want another opinion first, ask me.',
    ],
  },
  {
    path: '/hub/loss-runs',
    lines: [
      // ⚠️ HE CAN ACTUALLY START ONE NOW (2026-09-07), so the line is allowed to
      // offer it. Before that he could only open the page and get out of the way,
      // and the opener was deliberately limited to the desk — an opener that
      // promises more than the menu delivers teaches people the mascot is decoration.
      "Hi, I'm Connor. Give me the insured and the carrier and I'll start one for you.",
      'I can put you through to the commercial lines desk if one has gone quiet.',
    ],
  },
  {
    path: '/hub/report-a-loss',
    lines: [
      "Hi, I'm Connor. Tell me the insured and the carrier and I'll start the notice.",
      'Once it is reported, the claim number is the bit worth chasing.',
    ],
  },
  {
    path: '/hub/inspector',
    lines: [
      "Hi, I'm Connor. Need help with a renewal account?",
      'Tell me the class and I’ll say which markets to line up for the renewal.',
    ],
  },
  {
    path: '/hub/policy-upload',
    lines: [
      "Hi, I'm Connor. Drop the dec page in and it fills the form for you.",
      'Anything the upload cannot read, the desk can sort out — just ask.',
    ],
  },
  {
    path: '/submission',
    lines: [
      "Hi, I'm Connor. Need help building your submission?",
      'Tell me the class and I’ll say which markets to start with.',
    ],
  },
  {
    path: '/hub/programs',
    lines: [
      "Hi, I'm Connor. Not sure which one to reach for? Ask me.",
      'Tell me what you are trying to write and I’ll point you at the right door.',
    ],
  },
];

const BUBBLE_SHOW_MS = 15_000;
const BUBBLE_GAP_MS = 20_000;

/**
 * Is the commercial lines desk open?
 *
 * ⚠️ THE MOST IMPORTANT LINE IN THE HUMAN CHAT IS THE ONE ABOUT WHEN SOMEBODY
 * WILL ANSWER. A chat that stays silent for fourteen hours because it was
 * opened at 9pm teaches a member the hub is where you get ignored — and that
 * lesson generalises to the parts that do work. Saying "you'll have an answer
 * in the morning" costs nothing and is true.
 *
 * Computed in Central regardless of where the member is sitting, because the
 * people answering are in Central. Holidays are not modelled: getting Christmas
 * wrong once a year is a smaller error than a hand-maintained calendar that
 * silently goes stale.
 */
const DESK_OPEN_HOUR = 8;
const DESK_CLOSE_HOUR = 17;
function deskIsOpen(now = new Date()): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    weekday: 'short',
    hour: 'numeric',
    hour12: false,
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  if (weekday === 'Sat' || weekday === 'Sun') return false;
  return hour >= DESK_OPEN_HOUR && hour < DESK_CLOSE_HOUR;
}

/**
 * What he opens with.
 *
 * ⚠️ TWO VERSIONS, BECAUSE ONE OF THEM WOULD BE A LIE TODAY. Josh asked for
 * "feel free to ask me anything commercial insurance related" — which is right
 * once the conversational path is on, and an over-promise while it is off. With
 * it off he answers appetite, which of our markets carry a carrier's paper, and
 * anything in the glossary; a box inviting "anything" and then failing on "what
 * is the class code for a bakery" teaches somebody the mascot is decoration,
 * which is the exact failure the page openers were written to avoid.
 *
 * It follows `smart`, so the day CONNOR_CONVERSATIONAL is switched on the copy
 * widens by itself rather than waiting for anybody to remember this file.
 */
const GREETING_GUIDED: Msg = {
  from: 'aas',
  text:
    "Hi, I'm Connor. Ask me about appetite, which of our markets carry a carrier's paper, " +
    'or what any commercial insurance term means — just type it. Or pick one of these.',
};

const GREETING_OPEN: Msg = {
  from: 'aas',
  text:
    "Hi, I'm Connor. Ask me anything commercial insurance related — appetite, a carrier's paper, " +
    'what a term means, or something you would ask a colleague. Or pick one of these.',
};

const GREETING = GREETING_GUIDED;

/**
 * Open Connor from anywhere — the rail, a hub tile — without threading a
 * setter through the tree. An event rather than context because the chat is
 * mounted beside the router, not inside it, so there is no common provider to
 * hang state on that wouldn't also re-render every page on every keystroke.
 */
export const CONNOR_OPEN = 'connor:open';
/**
 * Open Connor, optionally straight onto a past search.
 *
 * ⚠️ THE TIMESTAMP IS THE ID. A recent search has no key of its own — it is a
 * localStorage row identified by when it was asked — and passing the question
 * text instead would resume the wrong one the second time somebody asks about
 * the same insured.
 */
export const openConnor = (at?: number) =>
  window.dispatchEvent(new CustomEvent(CONNOR_OPEN, { detail: at ? { at } : undefined }));

export function AppetiteChat() {
  const navigate = useNavigate();
  const { session, email } = useAuth();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [autoBubble, setAutoBubble] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const [line, setLine] = useState(0);

  /**
   * The page's own lines first, then the general ones.
   *
   * The page lines lead because the first bubble is the only one most people
   * see — it shows on arrival, and someone who came to Supplementals to find a
   * form is better served by "need help finding a form?" than by an appetite
   * question they did not come for. The general lines still follow, because he
   * does answer appetite everywhere.
   */
  const { pathname } = useLocation();
  const openers = useMemo(() => {
    const hit = PAGE_OPENERS.filter((p) => pathname.startsWith(p.path)).sort(
      (a, b) => b.path.length - a.path.length,
    )[0];
    // OPENERS[0] introduces him by name and so does every page line — keeping
    // both would have him say "Hi, I'm Connor" twice in one rotation.
    return hit ? [...hit.lines, ...OPENERS.slice(1)] : OPENERS;
  }, [pathname]);

  /**
   * Is the commercial lines desk open, right now?
   *
   * ⚠️ RE-CHECKED ON A TIMER, NOT COMPUTED ONCE. Somebody with the hub open at
   * 4.55pm is exactly the person who most needs the dot to change — they are
   * about to ask a question and decide whether to wait for an answer. Rendered
   * once at mount, the indicator would still be promising a live desk at
   * midnight on a tab nobody closed.
   */
  const [deskOpen, setDeskOpen] = useState(() => deskIsOpen());
  useEffect(() => {
    const t = window.setInterval(() => setDeskOpen(deskIsOpen()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  /**
   * Put a past search back on screen.
   *
   * ⚠️ REPLAYED, NEVER RE-RUN — the same rule the in-chat list has always
   * followed. Re-running costs a call and can return something different,
   * because the appetite bundle moves underneath us. "What did it tell me on
   * Tuesday" has exactly one honest answer and it is the one they were shown.
   */
  const replayRecent = (r: RecentSearch) => {
    setCls(r.answer.classes[0]?.class ?? null);
    setMsgs([
      { from: 'you', text: r.question },
      { from: 'aas', text: `What I told you ${ago(r.at)} — replayed exactly, not re-run.` },
      { from: 'aas', answer: r.answer },
    ]);
    setStep('answered');
  };

  useEffect(() => {
    const onOpen = (e: Event) => {
      setOpen(true);
      const at = (e as CustomEvent<{ at?: number } | undefined>).detail?.at;
      if (!at) return;
      /*
        Read from storage rather than from `recent` state: the dashboard can
        fire this before the chat has ever been opened, so the in-component copy
        may still be empty.
      */
      const hit = loadRecent().find((r) => r.at === at);
      if (hit) replayRecent(hit);
    };
    window.addEventListener(CONNOR_OPEN, onOpen);
    return () => window.removeEventListener(CONNOR_OPEN, onOpen);
    // replayRecent only touches setters, which are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Speak straight away, then cycle: visible for 15s, quiet for 20s, next line.
   *
   * Rotating the line matters more than the timing — the same sentence
   * reappearing every half minute reads as a stuck banner, whereas a different
   * question each time reads as someone waiting to help. Closing the bubble
   * stops the cycle for good; that's a clear "leave me alone".
   */
  useEffect(() => {
    if (open || dismissed) return;
    let timer: ReturnType<typeof setTimeout>;

    const hide = () => {
      setAutoBubble(false);
      timer = setTimeout(next, BUBBLE_GAP_MS);
    };
    const next = () => {
      setLine((i) => (i + 1) % openers.length);
      setAutoBubble(true);
      timer = setTimeout(hide, BUBBLE_SHOW_MS);
    };

    // Back to the top of the list on arrival, so the page's own line is the one
    // that shows — landing mid-rotation on "I'm here if you need me" would waste
    // the only bubble most people see.
    setLine(0);
    setAutoBubble(true);
    timer = setTimeout(hide, BUBBLE_SHOW_MS);
    return () => clearTimeout(timer);
  }, [open, dismissed, openers]);

  const dismissBubble = () => {
    setAutoBubble(false);
    setDismissed(true);
  };

  // Hovering brings it back even after it's been dismissed — the mascot should
  // still answer when someone deliberately reaches for it.
  const bubble = !open && (hovered || autoBubble) ? openers[line % openers.length] : null;

  /**
   * The bubble line, split into a lead and the rest.
   *
   * ⚠️ SPLIT RATHER THAN STORED AS TWO FIELDS, because the openers are a single
   * rotating list that several page-specific sets feed into — turning every one
   * of them into a pair would be a wide change to data that reads perfectly
   * well as a sentence. Lines with no lead sentence render entirely in the
   * smaller style, which is the honest fallback: not every opener introduces
   * him, and bolding half of "Got an appetite question?" would emphasise
   * nothing in particular.
   */
  const bubbleParts = ((): { lead: string; rest: string } => {
    if (!bubble) return { lead: '', rest: '' };
    /*
      ⚠️ ANY SHORT OPENING SENTENCE, NOT JUST HIS NAME. It first matched only
      "Hi, I'm Connor." — which was enough when every line introduced him and
      wrong the moment the rotation became "You can ask me… <a thing he does>",
      where the lead is the half that should stay put while the rest changes.
      Capped at 30 characters so a single long sentence is never chopped in two
      and half of it emboldened for no reason.
    */
    const m = /^(.{3,30}?[.?!…])\s+(.+)$/s.exec(bubble);
    return m ? { lead: m[1]!, rest: m[2]!.trim() } : { lead: '', rest: bubble };
  })();

  /**
   * A conversation left half-finished, picked back up.
   *
   * ⚠️ READ ONCE, IN AN INITIALISER. Restoring in an effect would render the
   * greeting first and then replace it, so the member watches the app forget
   * and then remember — which reads worse than either.
   */
  const restored = useMemo(() => loadConnorSession(email), [email]);

  const [step, setStep] = useState<Step>((restored?.step as Step) ?? 'intent');
  const [intent, setIntent] = useState<Intent>('appetite');
  const [cls, setCls] = useState<string | null>(null);
  /**
   * Which classes have a real web questionnaire, as opposed to only the
   * carrier's PDF. Fetched once when the chat opens rather than per answer, and
   * an empty list simply means everyone gets the PDF route — the fallback is the
   * behaviour that shipped, so a failed fetch costs nothing.
   */
  const [webFormClasses, setWebFormClasses] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!open || !session || webFormClasses.size) return;
    api
      .listForms()
      .then((r) => setWebFormClasses(new Set(r.forms.map((f) => f.class))))
      .catch(() => undefined);
  }, [open, session, webFormClasses.size]);
  const hasWebForm = Boolean(cls && webFormClasses.has(cls));
  /** What Connor has gathered so far for a request he is starting. */
  const [startKind, setStartKind] = useState<{ id: string; name: string; href: string } | null>(null);
  const [startInsured, setStartInsured] = useState('');

  /** Whether the rest of the menu is showing. Collapses again on 'Back'. */
  /**
   * What Connor is doing right now, one line at a time.
   *
   * ⚠️ IT REPLACES ITSELF RATHER THAN ACCUMULATING. Three stacked lines of
   * "Checking our carrier guides… / Searching the web… / Looking it up in the
   * glossary…" is the thesis Josh asked not to have. The current step is the
   * only one that answers "what is it doing", and the answer that follows says
   * what it found.
   */
  const [stepNote, setStepNote] = useState('');

  /**
   * A request Connor has drafted, waiting on a decision.
   *
   * ⚠️ ITS OWN CARD, NOT THE TYPING BOX. The first attempt loaded the draft
   * into the composer, and Josh's reaction was the whole review: "Oh, it's in
   * the chat message box. That's confusing." Text sitting where you type reads
   * as text you wrote — you cannot tell whether it is a suggestion, something
   * to edit, or something already queued. A card with two buttons says exactly
   * what it is and exactly what happens next.
   *
   * ⚠️ NOT PERSISTED, DELIBERATELY. It lives in state and dies with a reload:
   * an unanswered "shall I send this?" resurrecting tomorrow would be a request
   * nobody remembers agreeing to.
   */
  const [deskDraft, setDeskDraft] = useState<string | null>(null);
  const [deskBusy, setDeskBusy] = useState(false);

  const [moreOpen, setMoreOpen] = useState(false);

  /**
   * A lookup has come back with nothing at some point in this conversation.
   *
   * ⚠️ SET FROM THE SERVER'S `unresolved`. It flips the first time a lookup
   * returns nothing, which is what "could not locate it" actually means — the
   * browser cannot tell a miss from an answer, because both arrive as prose.
   *
   * ⚠️ IT ONLY EVER TURNS ON. Once somebody has hit something we do not hold,
   * the button stays for the rest of the conversation. Withdrawing it because
   * the next question happened to land would be the app deciding they no longer
   * need help it just offered.
   */
  const [sawMiss, setSawMiss] = useState(false);

  const [draft, setDraft] = useState('');
  const [recent, setRecent] = useState<RecentSearch[]>([]);
  useEffect(() => {
    // Read when the panel opens rather than on mount: a search made in another
    // tab should be here too.
    if (open) setRecent(loadRecent());
  }, [open, step]);

  /** The question behind the answer on screen, for the outcome we may record. */
  const lastQuestion = useRef('');
  const [msgs, setMsgs] = useState<Msg[]>(() => (restored?.msgs as Msg[]) ?? [GREETING]);
  const endRef = useRef<HTMLDivElement>(null);

  /**
   * Whether the offer of a colleague has been earned yet.
   *
   * Two ways in, and they answer different halves of Josh's sentence — "isn't
   * able to locate what they're looking for" and "after a diligent effort".
   *
   * `sawMiss` is the first half: a lookup ran and returned nothing, which is
   * direct evidence we do not hold the answer.
   *
   * ⚠️ THE QUESTION COUNT IS THE SECOND HALF, AND IT EXISTS FOR THE NEED CONNOR
   * WAS NEVER GOING TO SERVE (Josh, 2026-09-09: "yeah add the three question
   * thing too"). "I need help with a claim on the Henderson account" produces no
   * miss — no lookup fails, because none of them is the right lookup — so a
   * miss-only gate would have made that member type their way to a person while
   * the button sat hidden. Three questions is a diligent effort by any reading,
   * whatever the answers were.
   *
   * ⚠️ A TALLY, NOT A COUNT OF `msgs`, AND THE FIRST VERSION WAS BROKEN FOR
   * EXACTLY THAT REASON. Deriving it from the transcript looked tidier and was a
   * no-op: `reset` and `backToMenu` both replace `msgs` with a single greeting,
   * and those are precisely the paths that lead back to the menu where the
   * button lives. The count was therefore always zero at the only moment it
   * could matter.
   *
   * ⚠️ AND IT SURVIVES "ask about another risk" ON PURPOSE. Three questions
   * across two topics is still three questions — the member has been trying.
   * Tying the tally to one thread would reset the effort every time they moved
   * on, which is the same bug in a politer form.
   *
   * Held in memory only. A reload starts it over, which is the acceptable end of
   * the trade: persisting it means writing session state for a button, and
   * backToMenu clears the stored conversation anyway.
   */
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const earnedDesk = sawMiss || questionsAsked >= ASK_BEFORE_DESK;

  /**
   * The conversational Connor, when this person is allowed it.
   *
   * ⚠️ IT REPLACES THE FREE-TEXT PATH, NOT THE MENU. The buttons still work
   * exactly as they did — they are a fast route through the common cases and
   * cost nothing. What changes is what happens when somebody types: instead of
   * a regex deciding paper-question from appetite-question, a model reads it,
   * calls the tools it needs, and can be asked a follow-up.
   *
   * Off for everybody until the flag and the allow-list both say otherwise, so
   * the state machine below is still what a member gets.
   */
  const [smart, setSmart] = useState(false);
  const convo = useRef<{ role: 'user' | 'assistant'; content: string }[]>(restored?.convo ?? []);
  useEffect(() => {
    if (!open || !session) return;
    api
      .connorAvailable()
      .then((r) => {
        setSmart(r.available);
        /*
          Swap the opening line if it is still the one we put there. Only when
          nothing else has been said — rewriting history mid-conversation would
          change a message the member has already read.
        */
        if (r.available) {
          setMsgs((prev) =>
            prev.length === 1 && prev[0]?.text === GREETING_GUIDED.text ? [GREETING_OPEN] : prev,
          );
        }
      })
      .catch(() => setSmart(false));
  }, [open, session]);

  /** The live human conversation, once one exists. */
  const [threadId, setThreadId] = useState<string | null>(restored?.threadId ?? null);

  /*
    ⚠️ SAVED ON THE TRANSCRIPT, NOT ON EVERY KEYSTROKE. `msgs` changes when
    something is actually said, which is the only moment worth persisting — and
    the greeting alone is not a conversation, so it is skipped rather than
    stored and resurrected on a machine nobody has spoken to Connor on.
  */
  useEffect(() => {
    if (!email) return;
    const said = msgs.length > 1 || Boolean(msgs[0]?.answer);
    if (!said) return;
    saveConnorSession({ email, msgs, convo: convo.current, step, threadId });
    // convo is a ref: it is written alongside msgs, so msgs is the honest trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs, step, threadId, email]);

  /**
   * How many staff replies we have already shown, so a poll appends only what
   * is new. Counting rather than diffing by id because the same person may
   * legitimately send the same words twice — "yes", "yes" — and a set would
   * silently swallow the second one.
   */
  const shownStaff = useRef(0);

  /**
   * Poll for the answer from Teams.
   *
   * ⚠️ POLLING, NOT REALTIME, AND DELIBERATELY. Supabase Realtime would be
   * tidier and would also make this depend on a socket staying up through
   * corporate proxies at a few hundred independent agencies. A GET every five
   * seconds while the window is open is boring, survives anything, and the
   * email in the reply handler covers the member who closed the tab — which is
   * most of them, most of the time.
   */
  useEffect(() => {
    if (!open || step !== 'human' || !threadId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const out = await api.hubChatPoll();
        if (cancelled || !out.threadId) return;
        const staff = out.messages.filter((m) => m.sender === 'staff');
        if (staff.length > shownStaff.current) {
          const fresh = staff.slice(shownStaff.current);
          shownStaff.current = staff.length;
          setMsgs((m) => [
            ...m,
            ...fresh.map((s) => ({
              from: 'aas' as const,
              // Named, not "Agents Alliance". A member who knows they are
              // talking to Cyndy asks Cyndy the next question, which is the
              // entire reason for doing this instead of a ticket form.
              text: s.author ? `${s.author.split('@')[0]}: ${s.body}` : s.body,
            })),
          ]);
        }
      } catch {
        // A failed poll is not worth a message in the window — the next one is
        // five seconds away, and an error bubble per lost packet is noise.
      }
    };

    const timer = window.setInterval(() => void tick(), 5000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, step, threadId]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, step, open]);

  const say = (...added: Msg[]) => setMsgs((m) => [...m, ...added]);

  const chooseIntent = (which: Intent) => {
    setIntent(which);
    say(
      { from: 'you', text: which === 'appetite' ? 'Just looking up appetite' : 'Walk me through it' },
      {
        from: 'aas',
        text:
          which === 'appetite'
            ? 'Tell me about the risk the way you’d tell a colleague — and give me the state if you have it.'
            : 'Then we start where every submission starts: appetite. Describe the risk the way you’d tell a colleague, with the state if you have it.',
      },
    );
    setStep('ask');
  };

  /**
   * Hand off to another part of the hub.
   *
   * Connor says where he is sending you before he sends you. A chat that
   * silently changes the page underneath someone reads as a misclick, and the
   * sentence is also the only record of what you asked for once the window
   * closes behind you.
   */
  const leaveFor = (to: string, said: string, then: string) => {
    say({ from: 'you', text: said }, { from: 'aas', text: then });
    setStep('done');
    // Let the line land before the page changes under them.
    window.setTimeout(() => {
      navigate(to);
      setOpen(false);
    }, 550);
  };

  const chooseTask = (task: Task) => {
    if (task === 'appetite' || task === 'guided') {
      chooseIntent(task);
      return;
    }

    /**
     * ⚠️ `recent` IS NOT IN MENU, AND THAT KILLED THE BUTTON. It is appended to
     * the choices list at render time, only when there is history to show — so
     * MENU.find() returned undefined and the non-null assertion below threw
     * before any of the branches ran. Clicking "Recent searches (4)" did
     * nothing at all, twice reported, because a thrown handler and a missing
     * handler are indistinguishable from the outside.
     *
     * The lesson is about the `!`, not about recent: it asserted a relationship
     * between two lists that nothing enforces, and the compiler believed it.
     * Handled before the lookup, and the lookup made safe, so the next task
     * added outside MENU degrades to a wrong label rather than a dead button.
     *
     * Now sits ABOVE the sign-in check, deliberately. Recent searches live in
     * this browser's localStorage and replay an answer already shown — there is
     * no call and nothing to authorise. Telling someone to sign in to see their
     * own history was never right; it was just where the branch happened to be.
     */
    if (task === 'recent') {
      say({ from: 'you', text: 'Show my recent searches' });
      setStep('recent');
      return;
    }

    const label = MENU.find((m) => m.task === task)?.label ?? 'That';

    // Everything below this line lives behind the hub's sign-in. Sending an
    // anonymous visitor to a route that will bounce them is worse than saying so.
    if (!session) {
      say(
        { from: 'you', text: label },
        {
          from: 'aas',
          text: 'That part of the hub is for Agents Alliance members — sign in at the top of the page and I’ll take you there.',
        },
      );
      setStep('done');
      return;
    }

    /*
      ⚠️ HE ANSWERS THIS ONE HIMSELF RATHER THAN OPENING THE GLOSSARY PAGE.
      Every other menu item that is not appetite is a door — Connor navigates
      and gets out of the way, because a wizard in a 380px bubble is worse than
      the page built for it. A definition is the exception: it is two sentences,
      it fits, and sending somebody to a page to read two sentences when they
      asked you a question is the thing that makes an assistant feel useless.
      The link to the full entry goes out with the answer either way.
    */
    if (task === 'start') {
      say(
        { from: 'you', text: 'Start a request for me' },
        { from: 'aas', text: 'Which one?' },
      );
      setStep('start-kind');
      return;
    }

    if (task === 'glossary') {
      say(
        { from: 'you', text: 'What does a term mean?' },
        {
          from: 'aas',
          text: "Type the term — 'retro date', 'COI', 'additional insured'. Abbreviations are fine, and so is 'X vs Y' if you want the difference between two.",
        },
      );
      setStep('ask-term');
      return;
    }

    if (task === 'paper') {
      say(
        { from: 'you', text: "Which markets carry a carrier's paper?" },
        {
          from: 'aas',
          text: "Name the carrier — Kinsale, Nautilus, Scottsdale — and I'll tell you which of our markets we have actually placed it through.",
        },
      );
      setStep('ask-paper');
      return;
    }

    if (task === 'human') {
      say(
        { from: 'you', text: label },
        {
          from: 'aas',
          text: deskIsOpen()
            ? 'Go ahead — type your question and I’ll put it in front of the commercial lines team. Their answer comes back right here, and by email too in case you’ve moved on.'
            : 'The commercial lines desk is open 8am–5pm Central, Monday to Friday. Ask anyway — it goes to them either way and you’ll have an answer in the morning, here and by email.',
        },
      );
      setStep('human');
      return;
    }

    if (task === 'portal') {
      /**
       * One question before the portal, not a detour for its own sake.
       *
       * An agent who already knows their markets doesn't need appetite and
       * resents being walked through it. An agent who doesn't will otherwise
       * build a whole submission and only then discover none of our carriers
       * write the class — which is the expensive order to find that out in.
       */
      say(
        { from: 'you', text: label },
        { from: 'aas', text: 'Before we start — do you already know the carriers you’ll be quoting with?' },
      );
      setStep('know-markets');
    } else if (task === 'commpass') {
      leaveFor('/new', label, 'Taking you to COMM-PASS, where a specialist picks it up from you.');
    } else if (task === 'supplemental') {
      leaveFor('/hub/supplementals', label, 'Here are the classes we hold a supplemental for — fill it in yourself, or send the insured a link.');
    } else if (task === 'inspector') {
      say(
        { from: 'you', text: label },
        {
          from: 'aas',
          text: INSPECTOR
            ? 'Opening the Commercial Account Inspector in a new tab — upload the renewal and it builds the exposure workbook and loss summary.'
            : "I can't find the Account Inspector from here — tell your Agents Alliance contact.",
        },
      );
      if (INSPECTOR) window.open(INSPECTOR.href, '_blank', 'noopener,noreferrer');
      setStep('done');
    }
  };

  /**
   * The whole point of the thing. Anything other than a real answer says so —
   * silence and invention are both worse than "I can't".
   */
  /**
   * "Who can I get Kinsale through?" is a different question from "will anyone
   * write a roofer in Texas", and the second answer is useless to the first.
   *
   * ⚠️ PATTERN-MATCHED, NOT CLASSIFIED BY A MODEL. It is a narrow, well-signposted
   * question — people say "through", "paper", "writing company" or "parent" —
   * and a deterministic rule is instant, free, and cannot decide to be creative.
   * If it guesses wrong the appetite answer still runs, so the cost of a miss is
   * a moment, not a wrong answer.
   */
  const PAPER_ASK =
    /\b(?:paper|writing compan\w*|parent compan\w*)\b|\bwho (?:can|do) (?:i|we) (?:get|place|write)\b|\bwhich (?:market|carrier|compan)\w* (?:carr|writ|hav)\w*\b|\b(?:place|get) .* through\b|\bwho (?:writes|carries|has)\b/i;

  /**
   * An unambiguous paper question, as opposed to one that merely sounds like it.
   *
   * ⚠️ THE DIFFERENCE DECIDES WHAT HAPPENS ON A MISS. "Who writes roofing
   * contractors in Texas" trips PAPER_ASK and is really an appetite question, so
   * finding no paper there must fall through quietly. "What parent companies
   * have access to Cover Whale" cannot be anything else — falling through there
   * produced Connor answering a question nobody asked, which is what Josh saw
   * on 2026-09-01.
   */
  const PAPER_STRONG = /\b(?:paper|writing compan\w*|parent compan\w*)\b|\b(?:place|get) .* through\b/i;

  /**
   * "What does X mean?" — the glossary question.
   *
   * ⚠️ IT REQUIRES DEFINITIONAL PHRASING, WHICH IS THE WHOLE SAFETY MARGIN.
   * Connor's day job is appetite, and "what is a restaurant" or "what do you
   * have for general liability" must not turn into a dictionary entry. So the
   * pattern matches how people ask to be *told a meaning* — "what is", "what
   * does ... mean", "define", "difference between" — rather than matching on a
   * term appearing anywhere in the sentence.
   */
  const DEFINE_ASK =
    /^\s*(?:what(?:'|’)?s|what is|what are|what does|what do)\b.*\b(?:mean|stand for)\b|^\s*(?:what(?:'|’)?s|what is|what are)\s+(?:an?|the)?\s*\w|^\s*(?:define|definition of|meaning of|explain)\b|\bdifference between\b|\bvs\.?\b|\bversus\b/i;

  /** Words people tack on the end of a term. Trimmed only from the tail. */
  const FILLER_TAIL =
    /\s+(?:coverage|cover|insurance|policy|policies|endorsement|clause|exclusion|form|letter|letters|mean|means)$/i;
  const LEADING_ARTICLE = /^(?:an?|the)\s+/i;
  const COMPARING = /\b(?:difference between|vs\.?|versus)\b/i;

  /**
   * The term inside a definitional question.
   *
   * ⚠️ STRIPPED RATHER THAN PASSED WHOLE. The glossary search weights an exact
   * abbreviation hit at 100, and "what does COI stand for" is not an exact
   * anything — feeding the whole sentence in scores it as a body-text match
   * against dozens of entries. Taking the wrapper off is what turns a vague hit
   * into the right one.
   */
  const defineSubject = (q: string): string =>
    q
      .replace(
        /^\s*(?:what(?:'|’)?s|what is|what are|what does|what do|define|definition of|meaning of|explain)\b/i,
        '',
      )
      .replace(/\b(?:mean|means|stand for|stands for)\b/gi, ' ')
      .replace(/\bdifference between\b/i, ' ')
      .replace(/[?.!,]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(LEADING_ARTICLE, '')
      .trim();

  /**
   * One phrase → one glossary entry, retrying without trailing filler.
   *
   * ⚠️ THE RETRY IS WHY "ordinance or law coverage" WORKS. People append
   * "coverage", "insurance", "policy" or "letter" to a term without thinking,
   * and the padded phrase matches nothing. Only a fixed list of tail words is
   * trimmed — trimming any trailing word would turn "the appetite for roofers"
   * into "appetite" and answer a live market question with a definition.
   */
  const lookOneTerm = (phrase: string): GlossaryTerm | null => {
    let p = phrase.replace(LEADING_ARTICLE, '').trim();
    if (!p) return null;
    for (let i = 0; i < 3; i++) {
      const hit = searchGlossary({ text: p })[0];
      if (hit) return hit;
      const trimmed = p.replace(FILLER_TAIL, '');
      if (trimmed === p) return null;
      p = trimmed;
    }
    return null;
  };

  /**
   * Answer from the glossary, or return false and let appetite have it.
   *
   * ⚠️ IT ANSWERS FROM THE GLOSSARY OR NOT AT ALL. There is no improvised
   * fallback: the value of answering from the glossary is that the answer is
   * the reviewed one the member would read if they clicked through. A miss
   * returns false so the appetite path still runs — "what is a machine shop"
   * reads as definitional and is really an appetite question.
   */
  const answerFromGlossary = (q: string): boolean => {
    const subject = defineSubject(q);
    if (!subject || subject.length > 60) return false;

    /*
      "A vs B" and "difference between A and B" want both entries, and each side
      has to be looked up separately — searching the whole phrase matches
      nothing, because no single term is called "deductible vs SIR".
    */
    let picked: GlossaryTerm[] = [];
    if (COMPARING.test(q)) {
      const seen = new Set<string>();
      picked = subject
        .split(/\s+(?:vs\.?|versus|and)\s+/i)
        .map(lookOneTerm)
        .filter((t): t is GlossaryTerm => Boolean(t) && !seen.has(t!.id) && Boolean(seen.add(t!.id)))
        .slice(0, 2);
    }
    if (!picked.length) {
      const one = lookOneTerm(subject);
      if (!one) return false;
      picked = [one];
    }

    for (const t of picked) {
      const parts = [`${t.term}${t.short ? ` (${t.short})` : ''} — ${t.def}`];
      if (t.detail) parts.push(`In practice: ${t.detail}`);
      if (t.confused) parts.push(`Often confused with: ${t.confused}`);
      say({ from: 'aas', text: parts.join('\n\n') });
    }

    /*
      ⚠️ THE SAME CAVEAT THE GLOSSARY PAGE NOW LEADS WITH, SAID EVERY TIME. A
      definition delivered in a chat reads far more like advice than the same
      words on a reference page do, and Connor is not permitted to say whether
      anything is covered. This sentence is the line between the two, so it is
      not conditional and it is not shortened.
    */
    say({
      from: 'aas',
      text:
        'That is what the term generally means. A particular policy can define it differently, and the wording in front of you is what decides a claim — if it matters on a live account, put it to the desk. ' +
        `Full entry: ${GLOSSARY_HREF}#${picked[0]!.id}`,
    });
    setStep('done');
    return true;
  };

  /**
   * The carrier names a question is asking about — plural, deliberately.
   *
   * ⚠️ IT USED TO RETURN ONE STRING FOR THE WHOLE SENTENCE. "What parent
   * companies have access to Cover Whale and which ones can access Canal?"
   * became "access to Cover Whale and ones access Canal", which matches nothing
   * in the book, so a perfectly good question returned nothing. Two carriers in
   * one sentence is a normal way to ask, not an edge case.
   *
   * Split first, strip second: the conjunctions are the only reliable signal of
   * where one name ends and the next begins.
   */
  const paperNamesFrom = (q: string): string[] => {
    /**
     * ⚠️ THE AUXILIARY VERBS WERE MISSING, AND IT SHOWED (Josh, 2026-09-01).
     * "What parent company will write Canal Insurance Company" reduced to "will
     * Canal Insurance" — "company" was stripped by compan\w*, "will" was not —
     * and Connor answered "I have nothing in our book written on will Canal
     * Insurance". The book holds Canal through five parents; the question never
     * reached it.
     *
     * This is the stop list growing a word at a time, which is the argument for
     * the conversational path replacing it: a model splitting a sentence into
     * carrier names does not need to be told about "will".
     */
    const STOP =
      /\b(?:who|which|what|can|could|will|would|should|do|does|did|i|we|us|get|place|write|writes|writing|written|market|markets|carrier|carriers|compan\w*|parent|paper|through|for|the|a|an|is|are|was|were|be|been|have|has|had|carry|carries|with|our|access|to|ones|one|also|any|of|on|in|and|or|but|about|me|my|you|your|there|that|this|it)\b/gi;
    return [
      ...new Set(
        q
          .replace(/[?.!]/g, ' ')
          .split(/\s+(?:and|or)\s+|[,;&]/i)
          .map((part) => part.replace(STOP, ' ').replace(/\s+/g, ' ').trim())
          .filter((name) => name.length >= 3),
      ),
    ].slice(0, 4);
  };

  /**
   * Answer a paper question, for however many carriers it names.
   *
   * Returns true when it said something worth saying. On a strong question it
   * also reports the ones it could NOT find — silence about Canal while
   * answering about Cover Whale reads as "Canal wasn't asked about".
   */
  const askAboutPaper = async (
    q: string,
    opts: { raw?: boolean; strong?: boolean } = {},
  ): Promise<boolean> => {
    // From the menu the message IS the name; stripping question words out of
    // "Western World" would leave nothing worth searching.
    const names = opts.raw ? [q.trim()] : paperNamesFrom(q);
    if (!names.length) return false;

    try {
      const found: string[] = [];
      const missing: string[] = [];
      for (const name of names) {
        const r = await api.appetitePaper(name);
        if (r.results.length) {
          for (const res of r.results) {
            found.push(`${res.paper} — ${res.markets.map((m) => `${m.name} (${m.n})`).join(', ')}`);
          }
        } else {
          missing.push(name);
        }
      }

      if (!found.length) {
        // Only speak up on an unmistakable paper question. On a vague one the
        // appetite answer that follows is probably what they wanted anyway.
        if (!opts.strong) return false;
        say({
          from: 'aas',
          text:
            `I have nothing in our book written on ${missing.join(' or ')}. Either we have not placed it, or it goes by another name in AMS360 — try the name exactly as it appears on the policy.`,
        });
        setStep('done');
        return true;
      }

      say({
        from: 'aas',
        text:
          `From our own book — markets we have actually placed that paper through, with how many policies:

${found.join('\n')}

` +
          (missing.length
            ? `I have nothing on ${missing.join(' or ')} — either we have not placed it, or it goes by another name in AMS360.\n\n`
            : '') +
          'That is placement history, not an appetite statement: a market that wrote it before may still decline this one.',
      });
      setStep('done');
      return true;
    } catch {
      return false;
    }
  };

  const send = async () => {
    const q = draft.trim();
    if (!q) return;
    setDraft('');
    say({ from: 'you', text: q });
    /*
      Counted here and only here: a typed question is the member trying. Clicking
      a menu item is them navigating, and counting navigation as effort would
      hand out the desk button for looking around.
    */
    setQuestionsAsked((n) => n + 1);

    /**
     * Relay mode. Connor stops answering and starts carrying.
     *
     * ⚠️ `notified: false` IS SAID OUT LOUD. It means the message was stored but
     * the Teams flow never accepted it, so nobody at AAS has been paged. From
     * the member's side that is indistinguishable from a delivered question
     * until the silence has already cost them a day — so we tell them, and give
     * them the email address that does not depend on a flow being up.
     */
    if (step === 'human') {
      await relaySend(q);
      return;
    }


    /**
     * Asked from the menu, the whole message is the carrier name — "Kinsale",
     * not a sentence to pattern-match. A miss says so rather than quietly
     * answering a different question the agent did not ask.
     */
    if (session && step === 'ask-paper') {
      setStep('thinking');
      if (await askAboutPaper(q, { raw: true })) return;
      say({
        from: 'aas',
        text: `I have nothing in our book written on ${q}. Either we have not placed it, or it goes by another name in AMS360.`,
      });
      setStep('done');
      return;
    }

    /*
      ⚠️ A MODE THE MEMBER PRESSED A BUTTON TO ENTER BEATS THE GENERAL
      CONVERSATION, AND THIS BLOCK SITS ABOVE THE SMART PATH FOR THAT REASON
      ALONE (fixed 2026-09-07). It shipped below it, which was invisible while
      CONNOR_CONVERSATIONAL looked like it was off — and it was not. The effect
      live: pick "Start a request for me", choose Loss Runs, type the insured,
      and the smart path answered first and returned, so the insured's name went
      to the model as a general question and Connor never asked for the carrier.

      Anything driven by `step` has to be handled before the catch-all. The
      catch-all is for messages that arrive with no mode attached.
    */
    /*
      Starting a request. Two typed answers and then the real page.

      ⚠️ HE SAYS WHAT HE IS ABOUT TO DO BEFORE HE DOES IT, AND THEN WAITS A
      BEAT. A chat that moves the ground under somebody mid-sentence reads as a
      misclick — the same reason the smart path pauses before navigating.
    */
    if (step === 'start-insured') {
      setStartInsured(q);
      say({ from: 'aas', text: 'And which carrier?' });
      setStep('start-carrier');
      return;
    }

    if (step === 'start-carrier') {
      const kind = startKind;
      if (!kind) {
        setStep('intent');
        return;
      }
      say({
        from: 'aas',
        text: `Opening ${kind.name} with ${startInsured} and ${q} filled in. Everything else — the policy period, who is asking, where the reports should go — is on the page, and you should check what I have put in before you send it.`,
      });
      const href = newFormHref(kind.href, { insured: startInsured, carrier: q });
      setStep('done');
      window.setTimeout(() => {
        navigate(href);
        setOpen(false);
      }, 900);
      return;
    }

    /*
      Asked from the menu, the whole message is the term. No wrapper to strip
      and no ambiguity to guard against — "COI" typed here means one thing.
    */
    if (step === 'ask-term') {
      if (answerFromGlossary(`define ${q}`)) return;
      say({
        from: 'aas',
        text: `The glossary does not have “${q}”. I would rather say that than make one up — if it is a real term we are missing, tell your Agents Alliance contact and it can be added.`,
      });
      setStep('done');
      return;
    }

    /**
     * The conversational path. One call, whatever the question is.
     *
     * ⚠️ EVERYTHING BELOW THIS BLOCK IS THE FALLBACK, AND STAYS. `smart` is
     * false for every member today, so the regex, the paper lookup and the
     * appetite call are still what runs. This is the new road being built
     * alongside the old one, not over it.
     */
    if (session && smart) {
      setStep('thinking');
      convo.current = [...convo.current, { role: 'user' as const, content: q }].slice(-20);
      try {
        const out = await connorTurn(
          convo.current,
          session?.access_token ?? null,
          (label) => setStepNote(label),
        );
        setStepNote('');
        convo.current = [...convo.current, { role: 'assistant' as const, content: out.reply }];
        if (out.reply) say({ from: 'aas', text: out.reply });
        setStep('ask');
        /*
          A lookup came back with nothing, so the member has now genuinely hit
          something we do not hold — the offer of a colleague is earned from
          here on. See `earnedDesk` — a miss earns it outright, without waiting
          for the question count.
        */
        if (out.unresolved) setSawMiss(true);
        // Connor asked for a page. Said first, then done — a chat that moves
        // the ground under someone reads as a misclick.
        const go = out.actions.find((a) => a.type === 'openPage');
        if (go && go.type === 'openPage') {
          window.setTimeout(() => {
            navigate(go.page);
            setOpen(false);
          }, 700);
        }

        /*
          ⚠️ THE DRAFT IS LOADED, NOT SENT. Connor used to offer to send a
          request to the desk and had no way to send anything. This puts his
          wording in the box, switches to relay mode, and stops — the member
          reads it and presses send, and the flow that already reaches the team
          does the sending. Nobody should believe a request has gone because a
          chatbot said it would.
        */
        const desk = out.actions.find((a) => a.type === 'askDesk');
        if (desk && desk.type === 'askDesk') {
          /*
            ⚠️ NO MESSAGE OF OUR OWN HERE. The prompt already has Connor say he
            has put it in front of the desk and that it needs sending, so adding
            a second bubble saying the same thing was two consecutive messages
            with one instruction between them (Josh, 2026-09-07: "where
            below?"). The fix for "where" is not more words — it is putting the
            cursor in the box, which the browser scrolls into view for free.
          */
          setDeskDraft(desk.message);
          // Connor has judged that this needs a person. Whatever the lookups
          // did, the button must not then be missing from the menu behind him.
          setSawMiss(true);
        }
      } catch (err) {
        setStepNote('');
        /*
          ⚠️ AN OUTAGE EARNS THE BUTTON TOO, and deliberately on different
          grounds from `unresolved`. cameBackEmpty() refuses to treat a failed
          lookup as evidence we hold nothing — that is about not blaming the
          data. This is about the member: they asked, they got nothing, and
          making them argue their way to a colleague because our own request
          fell over would be the app protecting itself. The message below names
          the button, so it also has to be there.
        */
        setSawMiss(true);
        say({
          from: 'aas',
          text:
            err instanceof ApiError && err.status === 502
              ? 'That one did not come back. Ask me again, or put it to the team with “Chat with someone at Agents Alliance”.'
              : 'Something went wrong there. Try again in a moment.',
        });
        setStep('ask');
      }
      return;
    }

    /*
      A definitional question is answered from the glossary before anything
      else, because it is the only interpretation that fits. A miss falls
      through quietly: "what is a machine shop" reads as definitional and is
      really an appetite question.
    */
    if (DEFINE_ASK.test(q) && answerFromGlossary(q)) return;

    /*
      A bare abbreviation, on its own, with no question around it.

      ⚠️ ABBREVIATIONS ONLY, AND ONLY AS THE WHOLE MESSAGE. "COI" typed alone
      cannot mean anything but "what is a COI" — nobody asks for appetite by
      typing three capital letters. A bare *word* is a different matter:
      "umbrella" or "restaurants" typed alone is far more likely to be an
      appetite question, so those are deliberately not caught here.
    */
    if (!/\s/.test(q) && ABBREVIATIONS.has(q.replace(/[?.!]/g, '').toLowerCase())) {
      if (answerFromGlossary(`define ${q}`)) return;
    }

    // Try the paper lookup first when the wording points that way. A vague
    // match falls through to appetite; an unmistakable one says it found
    // nothing rather than quietly answering something else.
    if (session && PAPER_ASK.test(q)) {
      setStep('thinking');
      if (await askAboutPaper(q, { strong: PAPER_STRONG.test(q) })) return;
      setStep('ask');
    }

    if (!session) {
      say({
        from: 'aas',
        text: 'Appetite guidance is for Agents Alliance members — sign in at the top of the page and ask me again.',
      });
      setStep('done');
      return;
    }

    setStep('thinking');
    try {
      const answer = await api.appetiteAsk(q);
      // The class drives the supplemental offer, so remember what we matched.
      setCls(answer.classes[0]?.class ?? null);
      say({ from: 'aas', answer });
      rememberSearch(q, answer);
      lastQuestion.current = q;

      if (answer.noClassMatch && !answer.specialistMarkets.length) {
        setStep('ask');
        return;
      }
      if (intent === 'guided') {
        say({ from: 'aas', text: 'Ready to get the quote underway?' });
        setStep('offered-form');
      } else {
        setStep('answered');
      }
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 401
          ? 'Appetite guidance is for members — sign in at the top of the page and ask me again.'
          : err instanceof ApiError && err.status === 503
            ? "The appetite data isn't loaded on this server. Tell your Agents Alliance contact — that's a fixable one."
            : "Something went wrong looking that up. Try again, and if it keeps happening tell your Agents Alliance contact.";
      say({ from: 'aas', text: msg });
      setStep('ask');
    }
  };

  /**
   * Hand off to the questionnaire.
   *
   * Three outcomes, in descending order of usefulness: the class has a web form,
   * so Connor takes them straight into it; the class only has the carrier's PDF,
   * so he opens that; or we hold nothing, and he says so. He never implies a
   * form exists that doesn't.
   */
  /**
   * Ask how it went.
   *
   * ⚠️ THIS IS THE ONLY WAY WE EVER LEARN WHETHER THE GUIDANCE WAS ANY GOOD
   * (Josh, 2026-08-30). Once a submission leaves for a market, nothing comes
   * back to us — that is the same wall that makes bind rate unmeasurable. An
   * agent telling us "that bound with Utica" or "they declined it" is the whole
   * feedback loop, and it costs them one tap.
   *
   * Asked, never required. Someone who ignores it has lost nothing.
   */
  const offerOutcome = () => {
    say({
      from: 'aas',
      text: 'Good luck with it. If you remember, tell me how it goes — that is how this guidance gets better, and nothing else tells us.',
    });
    setStep('outcome');
  };

  const recordOutcome = (outcome: 'bound' | 'declined' | 'working') => {
    const said = outcome === 'bound' ? 'It bound' : outcome === 'declined' ? 'It was declined' : 'Still working it';
    say({ from: 'you', text: said });
    void api
      .appetiteOutcome({
        question: lastQuestion.current,
        className: cls,
        outcome,
      })
      .catch(() => undefined); // Telling us is a favour; never make it fail visibly.
    say({
      from: 'aas',
      text:
        outcome === 'bound'
          ? "That's the most useful thing you could have told me — noted against this class."
          : outcome === 'declined'
            ? 'Noted. A decline against a class our guides liked is exactly what we need to see.'
            : "Noted — tell me when you know, and I'll log it.",
    });
    setStep('done');
  };

  /**
   * Actually create the submission, with the class already on it.
   *
   * ⚠️ IT USED TO JUST NAVIGATE (Josh, 2026-08-30): "it needs to actually start
   * a new submission, not simply take you to the screen where you then have to
   * hit Start a new submission." Quite right — a button called "start a
   * submission" that starts nothing is a redirect wearing a verb.
   *
   * Carrying the class across matters as much as the click it saves. The build
   * screen only offers the supplemental once it knows the class, so an agent
   * who has just been told "Restaurants, 28 markets" arrives with the
   * questionnaire already waiting instead of retyping what Connor worked out.
   */
  const startSubmission = async () => {
    say({ from: 'you', text: 'Yes, start a submission' });
    try {
      const { id } = await api.portalCreate({ className: cls ?? undefined });
      say({
        from: 'aas',
        text: cls
          ? `Started it, with ${cls.toLowerCase()} already set — so the supplemental is there when you want it. Documents first; they are worth more than everything else on the screen combined.`
          : 'Started it. Documents first — they are worth more than everything else on the screen combined.',
      });
      setStep('done');
      window.setTimeout(() => {
        navigate(`/submission/${encodeURIComponent(id)}`);
        setOpen(false);
      }, 550);
    } catch (e) {
      say({
        from: 'aas',
        text:
          e instanceof ApiError && e.status === 404
            ? "The Submission Portal isn't switched on for your agency yet — speak to your Agents Alliance contact."
            : "I couldn't start it just now. Try the Submissions tab on the left.",
      });
      setStep('done');
    }
  };

  const startQuote = () => {
    const form = cls ? BY_CLASS[cls]?.[0] : null;
    say({ from: 'you', text: "Yes, let's go" });

    if (cls && hasWebForm) {
      say({
        from: 'aas',
        text: `Opening the ${cls.toLowerCase()} questionnaire. Fill in what you know, and you can send the rest to your insured to finish.`,
      });
      navigate(`/hub/supplementals/${encodeURIComponent(cls)}`);
      setOpen(false);
      setStep('done');
      return;
    }

    if (form) {
      window.open(form.url, '_blank', 'noopener,noreferrer');
      say({
        from: 'aas',
        text: `Opened ${form.title} in a new tab — that's the carrier's own PDF; this class doesn't have a web form yet. Do you want to build this submission yourself, or hand it to us?`,
      });
    } else {
      say({
        from: 'aas',
        text: `I don't have a supplemental on file for ${cls ? cls.toLowerCase() : 'that class'} yet. You can still start the submission and we'll sort the paperwork out with you. Build it yourself, or hand it to us?`,
      });
    }
    setStep('routing');
  };

  const reset = () => {
    setStep('ask');
    setCls(null);
    setMsgs([{ from: 'aas', text: 'Sure — tell me about the next one.' }]);
  };

  /** All the way back to "what can I help you with?". */
  /**
   * Put a message in front of a person at Agents Alliance.
   *
   * ⚠️ `notified: false` IS SAID OUT LOUD. It means the message was stored but
   * the Teams flow never accepted it, so nobody has been paged. From the
   * member's side that is indistinguishable from a delivered question until the
   * silence has already cost them a day.
   */
  const relaySend = async (text: string) => {
    try {
      const out = await api.hubChatSend(text, { page: window.location.pathname });
      setThreadId(out.threadId);
      if (!out.notified) {
        say({
          from: 'aas',
          text:
            'I saved that, but I could not reach the team just now — nobody has been paged, so please do not sit waiting on it. ' +
            'Email CLHelp@agentsalliance.biz and you will get a person straight away.',
        });
      }
      return out.notified;
    } catch {
      say({
        from: 'aas',
        text:
          'That did not send. Nothing has reached anyone at Agents Alliance — email CLHelp@agentsalliance.biz instead and someone will pick it up.',
      });
      return false;
    }
  };

  const sendDeskDraft = async () => {
    if (!deskDraft) return;
    setDeskBusy(true);
    say({ from: 'you', text: deskDraft });
    const ok = await relaySend(deskDraft);
    setDeskDraft(null);
    setDeskBusy(false);
    /*
      Relay mode from here: the desk's reply arrives in this same window, and
      anything else the member types should go to them rather than back to the
      appetite engine mid-conversation.
    */
    setStep('human');
    if (ok) {
      say({ from: 'aas', text: 'Sent. Their reply comes back here — you do not need to wait on this screen.' });
    }
  };

  const backToMenu = () => {
    setStep('intent');
    setMoreOpen(false);
    /*
      "What else can I help you with?" is a new conversation, so the stored one
      goes with it — otherwise a reload would drag back the thread the member
      had just deliberately left.
    */
    convo.current = [];
    clearConnorSession();
    setCls(null);
    setMsgs([{ from: 'aas', text: 'What else can I help you with?' }]);
  };

  const choices = useMemo(() => {
    if (step === 'intent') {
      /*
        ⚠️ FOUR BUTTONS, NOT ELEVEN. The ones left out are all reachable by
        typing — free text already goes to appetite, "who carries X's paper" is
        pattern-matched, "what does X mean" reaches the glossary — so showing
        them made the box look like a directory rather than something you talk
        to. The rest are one click away rather than gone, because taking away a
        door somebody has learned is worse than showing one too many.
      */
      const shown = MENU.filter(
        (m) => (m.upfront || moreOpen) && (!m.needsEffort || earnedDesk),
      );
      const items = shown.map((m) => ({
        label: m.label,
        primary: m.primary,
        onClick: () => chooseTask(m.task),
      }));
      // Only offered when there is history — an empty list is a dead end.
      if (moreOpen && recent.length) {
        items.push({ label: `Recent searches (${recent.length})`, primary: false, onClick: () => chooseTask('recent') });
      }
      if (!moreOpen) {
        items.push({ label: 'Something else…', primary: false, onClick: () => setMoreOpen(true) });
      }
      return items;
    }

    /**
     * In relay mode the only button is the way out. Offering "appetite
     * guidance" mid-conversation would invite a member to type their next
     * sentence at a machine while a colleague is halfway through answering the
     * last one.
     */
    if (step === 'human') {
      return [
        // Only once a thread exists — before that there is nothing to open, and
        // a button that leads to an empty record is worse than no button.
        ...(threadId
          ? [
              {
                label: 'Open the full transcript',
                primary: false,
                onClick: () => {
                  navigate(`/hub/chat/${threadId}`);
                  setOpen(false);
                },
              },
            ]
          : []),
        {
          label: 'Back to Connor',
          primary: false,
          onClick: () => {
            say({
              from: 'aas',
              text: threadId
                ? 'Your question is still with the team — their answer will land here and in your inbox. What else can I look up?'
                : 'No problem. What can I look up?',
            });
            setStep('intent');
          },
        },
      ];
    }

    if (step === 'recent') {
      /**
       * Each entry replays the answer that was actually shown, rather than
       * re-running the search. Re-running costs a call and can return something
       * different — the bundle moves — and "what did it tell me on Tuesday" has
       * exactly one honest answer.
       */
      return [
        ...recent.slice(0, 8).map((r) => ({
          label: `${r.question} · ${ago(r.at)}`,
          primary: false,
          onClick: () => {
            replayRecent(r);
          },
        })),
        { label: 'Back', primary: false, onClick: backToMenu },
      ];
    }
    /*
      ⚠️ BUTTONS FOR THE KIND, TYPING FOR THE FACTS. The kind is a closed set of
      three, and asking somebody to type "loss run" so it can be pattern-matched
      is inventing an opportunity to fail. The insured and the carrier are open
      text and have to be typed, which is also why they are the only two things
      he asks for.
    */
    if (step === 'start-kind') {
      return [
        ...STARTABLE.map((m) => ({
          label: m.name,
          primary: m.id === 'lossruns',
          onClick: () => {
            setStartKind({ id: m.id, name: m.name, href: m.href });
            say(
              { from: 'you', text: m.name },
              { from: 'aas', text: 'Who is the insured?' },
            );
            setStep('start-insured');
          },
        })),
        { label: 'Back', primary: false, onClick: backToMenu },
      ];
    }

    if (step === 'know-markets') {
      return [
        {
          label: 'Yes, straight to the portal',
          primary: true,
          onClick: () =>
            leaveFor(
              '/submission',
              'Yes, I know my markets',
              'Then let’s build it. Documents first — they’re worth more than everything else on the screen combined.',
            ),
        },
        {
          label: 'No, help me look up appetite',
          onClick: () => {
            say(
              { from: 'you', text: 'No, help me look up appetite' },
              {
                from: 'aas',
                text: 'Good call — better to know now than after you’ve built it. Describe the risk the way you’d tell a colleague, with the state if you have it.',
              },
            );
            setIntent('guided');
            setStep('ask');
          },
        },
      ];
    }
    if (step === 'answered') {
      /**
       * ⚠️ ASK HOW THEY ARE QUOTING, NOT WHETHER THEY WANT HELP (Josh,
       * 2026-08-30). The two paths need completely different things. An agent
       * rating online wants the supplemental and nothing else — offering them a
       * submission portal is noise. An agent going to an underwriter wants the
       * whole package. "Help me submit it" flattened both into one road and
       * dropped everyone at the same place.
       */
      return [
        {
          label: 'Quoting online',
          primary: true,
          onClick: () => {
            say(
              { from: 'you', text: "I'll be quoting online" },
              {
                from: 'aas',
                text: hasWebForm
                  ? `Then all you need is the ${cls?.toLowerCase() ?? 'class'} supplemental. Which would you rather have?`
                  : 'Then all you need is the supplemental — let me get it for you.',
              },
            );
            setStep('pick-form');
          },
        },
        {
          label: 'Submitting to an underwriter',
          onClick: () => {
            say(
              { from: 'you', text: "I'm submitting to an underwriter" },
              {
                from: 'aas',
                text: 'Then the Submission Portal is worth your time — drop the documents in, it reads them, fills the account in, and sends it to your markets with everything attached. Want to start it here?',
              },
            );
            setStep('how-quoting');
          },
        },
        { label: 'Ask about another risk', onClick: reset },
      ];
    }

    if (step === 'how-quoting') {
      return [
        {
          label: 'Yes, start a submission',
          primary: true,
          onClick: () => void startSubmission(),
        },
        {
          label: 'Just the supplemental',
          onClick: () => {
            say({ from: 'you', text: 'Just the supplemental' });
            setStep('pick-form');
          },
        },
        { label: 'Neither, thanks', onClick: () => offerOutcome() },
      ];
    }

    if (step === 'pick-form') {
      const form = cls ? BY_CLASS[cls]?.[0] : null;
      const choices: { label: string; primary?: boolean; onClick: () => void }[] = [];
      if (cls && hasWebForm) {
        choices.push({
          label: 'Fillable web form',
          primary: true,
          onClick: () =>
            leaveFor(
              `/hub/supplementals/${encodeURIComponent(cls)}`,
              'The fillable web form',
              'Opening it. Fill in what you know and send the rest to your insured to finish.',
            ),
        });
      }
      if (form) {
        choices.push({
          label: "The carrier's PDF",
          primary: !hasWebForm,
          onClick: () => {
            window.open(form.url, '_blank', 'noopener,noreferrer');
            say({ from: 'you', text: "The carrier's PDF" }, { from: 'aas', text: `Opened ${form.title} in a new tab.` });
            offerOutcome();
          },
        });
      }
      if (!choices.length) {
        return [
          {
            label: 'Got it',
            primary: true,
            onClick: () => {
              say({
                from: 'aas',
                text: `I don't have a supplemental on file for ${cls ? cls.toLowerCase() : 'that class'} yet — the carrier's own site will have one.`,
              });
              offerOutcome();
            },
          },
        ];
      }
      return choices;
    }

    if (step === 'outcome') {
      return [
        { label: 'It bound', primary: true, onClick: () => recordOutcome('bound') },
        { label: 'It was declined', onClick: () => recordOutcome('declined') },
        { label: 'Still working it', onClick: () => recordOutcome('working') },
        { label: 'Ask about another risk', onClick: reset },
      ];
    }
    if (step === 'offered-form') {
      return [
        { label: "Yes, let's go", onClick: startQuote, primary: true },
        {
          label: 'Not right now',
          onClick: () => {
            say({ from: 'you', text: 'Not right now' }, { from: 'aas', text: "No problem — I'm here when you are." });
            setStep('done');
          },
        },
      ];
    }
    if (step === 'routing') {
      return [
        {
          label: "I'll do it myself",
          onClick: () =>
            leaveFor(
              '/submission',
              "I'll do it myself",
              "Opening the Submission Portal — build the package here and you'll see how it grades before it goes out.",
            ),
        },
        {
          label: 'Send it to AAS',
          primary: true,
          onClick: () => leaveFor('/new', 'Send it to AAS', 'Taking you to COMM-PASS — a specialist picks it up from there.'),
        },
      ];
    }
    if (step === 'done') {
      return [
        { label: 'Ask about another risk', onClick: reset, primary: true },
        { label: 'Something else', onClick: backToMenu },
      ];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    /**
     * ⚠️ EVERY VALUE THIS BUILDER READS BELONGS IN HERE. `recent` was missing,
     * so the menu was built from a stale closure holding an empty list — the
     * entry appeared only when something else happened to recompute this, and
     * opening it showed nothing at all. hasWebForm carries the same
     * risk and is listed for the same reason.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, cls, intent, session, recent, hasWebForm]);

  return (
    <>
      {!open && (
        <div className="peek-dock">
          {bubble && (
            <div className="peek-bubble" role="status">
              {/*
                ⚠️ THE CLOUD IS SVG BEHIND THE TEXT, NOT AN IMAGE AND NOT A
                BACKGROUND. The line changes — it rotates, and it is
                page-specific — so the shape has to grow with whatever is in it.
                preserveAspectRatio="none" lets one path fill whatever height
                the text needs; the lobes are drawn as wide, shallow arcs
                precisely so that stretching them a line or two reads as a
                slightly taller cloud rather than as a distorted one.
              */}
              <svg
                className="peek-cloud"
                viewBox="0 0 320 150"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="peekShade" x1="0" y1="0" x2="0" y2="1">
                    <stop offset=".52" stopColor="#FFFFFF" stopOpacity="0" />
                    <stop offset="1" stopColor="#CFE0F2" stopOpacity=".85" />
                  </linearGradient>
                </defs>
                <path className="peek-cloud-path" d={CLOUD_D} />
                {/* The shading rides the same path, so it can never drift off it. */}
                <path className="peek-cloud-shade" d={CLOUD_D} fill="url(#peekShade)" />
              </svg>

              {/* Comic emphasis, outside the upper-left edge. Decorative only. */}
              <span className="peek-marks" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>

              <div className="peek-text">
                {bubbleParts.lead && <b>{bubbleParts.lead}</b>}
                {bubbleParts.rest && <span>{bubbleParts.rest}</span>}
              </div>

              <button
                className="peek-bubble-x"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissBubble();
                }}
                aria-label="Dismiss"
              >
                {/* An icon, not the × character — it renders at a different
                    weight in every font and cannot be stroked to match. */}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
          )}
          <button
            className="pelican-peek"
            onClick={() => setOpen(true)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            aria-expanded={false}
            aria-label="Chat with Connor about carrier appetite"
          >
            <YardSign className="peek-sign" />
            <ConnorPeek className="pelican-peek-svg" />
          </button>
        </div>
      )}

      {open && (
        <div className="chat-dock">
          <ConnorStanding />
          <div className="chat-panel" role="dialog" aria-label="Connor, the appetite assistant">
            <div className="chat-head">
              <div>
                {/*
                  ⚠️ THE DOT IS THE DESK, NOT CONNOR (Josh, 2026-08-31: green
                  8-5 Central, red and "Offline" outside it). Connor himself
                  never sleeps — the appetite bundle answers at 2am exactly as
                  it does at noon — so an unqualified "Offline" beside his name
                  would tell members the one thing that always works is down,
                  and they would stop asking. Hence the word "team", and the
                  sub-line saying plainly what is still available.

                  It was a hardcoded green pulse before this, which promised
                  availability at every hour of the day.
                */}
                <div className="chat-title">
                  Connor{' '}
                  <span className={`chat-live${deskOpen ? '' : ' is-off'}`} aria-hidden="true" />
                  {!deskOpen && <span className="chat-status">Team offline</span>}
                </div>
                <div className="chat-sub">
                  {deskOpen
                    ? 'Appetite — which of our markets fit this risk?'
                    : 'Appetite answers any time. The team replies from 8am Central.'}
                </div>
              </div>
              {/* Always available, not just at the end of a flow. Getting stuck
                  halfway — wrong turn, wrong class, changed your mind — is the
                  common case, and closing the window to escape loses the thread
                  entirely. */}
              <button className="chat-restart" onClick={backToMenu} title="Start over" aria-label="Start over">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 12a8 8 0 11-2.3-5.7M20 4v4h-4" />
                </svg>
              </button>
              <button className="chat-x" onClick={() => setOpen(false)} aria-label="Close">
                ×
              </button>
            </div>

            <div className="chat-body">
              {msgs.map((m, i) =>
                m.answer ? (
                  <AppetiteAnswerCard key={i} answer={m.answer} />
                ) : (
                  <div key={i} className={`chat-msg is-${m.from}`}>
                    <ChatText text={m.text ?? ''} />
                  </div>
                ),
              )}

              {step === 'thinking' && stepNote && (
                <div className="chat-step" role="status">
                  <span className="chat-step-dot" aria-hidden="true" />
                  {stepNote}
                </div>
              )}

              {step === 'thinking' && !stepNote && (
                <div className="chat-msg is-aas is-thinking" role="status">
                  <span className="ap-typing" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  Checking the class map and the appetite guides…
                </div>
              )}

              {/* Six wrapped pills read as a scatter; the opening menu stacks
                  instead, so it looks like a list of things he can do. */}
              {/*
                ⚠️ A DECISION, NOT A MESSAGE. It is deliberately not a chat
                bubble and deliberately not the typing box: both of those read
                as something already said. This reads as something waiting on
                you, which is what it is.
              */}
              {deskDraft && (
                <div className="chat-desk">
                  <b>Send this to the commercial lines desk?</b>
                  <p>{deskDraft}</p>
                  <div className="chat-desk-act">
                    <button
                      className="btn btn-primary btn-sm"
                      type="button"
                      disabled={deskBusy}
                      onClick={() => void sendDeskDraft()}
                    >
                      {deskBusy ? 'Sending…' : 'Send it'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      disabled={deskBusy}
                      onClick={() => setDeskDraft(null)}
                    >
                      Not now
                    </button>
                  </div>
                  <span>Goes to a person at Agents Alliance. Their reply comes back here.</span>
                </div>
              )}

              {choices.length > 0 && (
                <div className={`chat-choices${step === 'intent' ? ' is-menu' : ''}`}>
                  {choices.map((c) => (
                    <button
                      key={c.label}
                      className={`chat-choice${c.primary ? ' is-primary' : ''}`}
                      onClick={c.onClick}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* ⚠️ ALWAYS AVAILABLE, NOT ONLY AT THE 'ask' STEP (Josh,
                2026-08-31): "we also should have just a chat box where they can
                start typing to ask a question". Buttons are a shortcut through
                the common path; they are not the only way in, and an agent
                halfway down a menu who thinks of a different question should be
                able to just type it. */}
            {step !== 'thinking' && (
              <div className="chat-foot">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void send()}
                  placeholder={
                    step === 'human'
                      ? 'Type your question for the commercial lines team'
                      : "Ask me anything — a risk, or who writes a carrier's paper"
                  }
                  aria-label={step === 'human' ? 'Message Agents Alliance' : 'Ask Connor'}
                />
                <button className="btn btn-primary" onClick={() => void send()} disabled={!draft.trim()}>
                  {step === 'human' ? 'Send' : 'Ask'}
                </button>
              </div>
            )}

            {/* The appetite disclaimer is wrong in relay mode — nothing here is
                a bundle lookup, it is a colleague typing. Saying "guidance only"
                over a person's answer reads as hedging their words. */}
            <div className="chat-note">
              {step === 'human' ? (
                threadId ? (
                  <>
                    With the commercial lines desk · {threadId}. The reply lands here and in your
                    inbox — you can close this.
                  </>
                ) : (
                  <>Goes to a person at Agents Alliance, not to a bot. Open 8am–5pm Central, Mon–Fri.</>
                )
              ) : (
                <>
                  Guidance only — carrier appetite changes constantly, and a listing here is not a
                  commitment to quote.
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
